
(() => {
  const clamp = (value, min, max) =>
    Math.max(min, Math.min(value, max));

  const escapeHTML = (value) =>
    String(value ?? '').replace(/[&<>"']/g, char => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    })[char]);

  const initialize = async section => {
    if (section.dataset.dwInitialized) return;

    const store = window.DesignWallStore;
    if (!store) {
      console.error('Design Wall global script is missing.');
      return;
    }

    section.dataset.dwInitialized = '1';

    const canvas = section.querySelector('[data-dw-canvas]');
    const summary = section.querySelector('[data-dw-summary]');
    const summaryItems = section.querySelector('[data-dw-summary-items]');
    const notice = section.querySelector('[data-dw-notice]');
    const toggle = section.querySelector('[data-dw-summary-toggle]');
    const root = (section.dataset.shopRoot || '/').replace(/\/?$/, '/');
    const cache = new Map();

    const getItems = () => store.read();
    const persist = items => store.write(items);

    const fetchProduct = handle => {
      if (!cache.has(handle)) {
        cache.set(
          handle,
          fetch(`${root}products/${encodeURIComponent(handle)}.js`)
            .then(response => {
              if (!response.ok) throw Error('Product unavailable');
              return response.json();
            })
        );
      }

      return cache.get(handle);
    };

    const imageUrl = product =>
      typeof product.featured_image === 'string'
        ? product.featured_image
        : product.featured_image?.src || product.images?.[0] || '';

    const removeItem = handle => {
      persist(getItems().filter(item => item.handle !== handle));
    };

    const render = async () => {
      const items = getItems();

      section.querySelector('[data-dw-count]').textContent = items.length;
      section.querySelector('[data-dw-empty]').hidden = !!items.length;

      canvas.querySelectorAll('.dw__card').forEach(card => card.remove());
      summaryItems.replaceChildren();

      const results = await Promise.all(
        items.map(async item => {
          try {
            return { item, product: await fetchProduct(item.handle) };
          } catch {
            return { item, error: true };
          }
        })
      );

      if (JSON.stringify(items) !== JSON.stringify(getItems())) return;

      results.forEach(({ item, product, error }, index) => {
        if (error) {
          const row = document.createElement('div');
          row.className = 'dw__summary-item';
          row.textContent = `${item.handle}: product unavailable. `;

          const remove = document.createElement('button');
          remove.type = 'button';
          remove.textContent = 'Remove';
          remove.onclick = () => removeItem(item.handle);
          row.append(remove);
          summaryItems.append(row);
          return;
        }

        const img = imageUrl(product);
        const card = document.createElement('article');
        card.className = 'dw__card';
        card.dataset.handle = item.handle;

        const x = clamp(
          Number(item.x) || 0,
          0,
          Math.max(0, canvas.clientWidth - 150)
        );

        const y = clamp(
          Number(item.y) || 0,
          0,
          Math.max(0, canvas.clientHeight - 190)
        );

        const width = clamp(
          Number(item.width) || 290,
          150,
          Math.max(150, canvas.clientWidth - x)
        );

        const height = clamp(
          Number(item.height) || 345,
          190,
          Math.max(190, canvas.clientHeight - y)
        );

        Object.assign(card.style, {
          left: `${x}px`,
          top: `${y}px`,
          width: `${width}px`,
          height: `${height}px`,
          zIndex: String(index + 1)
        });

        const productLink =
          `${root}products/${encodeURIComponent(item.handle)}`;

        card.innerHTML = `
          <div class="dw__card-top" data-drag>
            ⠿ Drag to move
            <button
              class="dw__card-remove"
              type="button"
              data-remove
              aria-label="Remove ${escapeHTML(product.title)}"
            >×</button>
          </div>

          <div class="dw__card-image" data-drag>
            ${img
              ? `<img src="${escapeHTML(img)}"
                       alt="${escapeHTML(product.title)}"
                       loading="lazy" data-product-id="${product.id}">`
              : ''}
          </div>

          <div class="dw__card-foot">
            <a class="dw__card-name"
               href="${escapeHTML(productLink)}">
              ${escapeHTML(product.title)}
            </a>

            <button
              type="button"
              class="dw__card-view"
              data-focus-item
            >
              Add to cart / Options
            </button>
            <button
              type="button"
              class="dw__card-print"
              data-product-id="${product.id}"
            >
              Print and View
            </button>
          </div>

          <button
            class="dw__resize"
            data-resize
            type="button"
            aria-label="Resize ${escapeHTML(product.title)}"
            title="Drag to resize"
          ></button>
        `;

        card.querySelector('[data-remove]').onclick =
          () => removeItem(item.handle);

        card.querySelector('[data-focus-item]').onclick = () => {
          summary.hidden = false;
          toggle.setAttribute('aria-expanded', 'true');

          const row = [...summaryItems.children].find(
            element => element.dataset.summaryHandle === item.handle
          );

          row?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
          });
        };

        const beginDrag = (event, resizing) => {
          if (event.button !== 0 ||
              event.target.closest('[data-remove]')) return;

          event.preventDefault();

          const startX = event.clientX;
          const startY = event.clientY;
          const originalX = card.offsetLeft;
          const originalY = card.offsetTop;
          const originalWidth = card.offsetWidth;
          const originalHeight = card.offsetHeight;

          card.style.zIndex = '10';

          const handle = event.currentTarget;
          handle.setPointerCapture(event.pointerId);

          const move = e => {
            if (resizing) {
              card.style.width = clamp(
                originalWidth + e.clientX - startX,
                150,
                canvas.clientWidth - originalX
              ) + 'px';

              card.style.height = clamp(
                originalHeight + e.clientY - startY,
                190,
                canvas.clientHeight - originalY
              ) + 'px';
            } else {
              card.style.left = clamp(
                originalX + e.clientX - startX,
                0,
                Math.max(0, canvas.clientWidth - card.offsetWidth)
              ) + 'px';

              card.style.top = clamp(
                originalY + e.clientY - startY,
                0,
                Math.max(0, canvas.clientHeight - card.offsetHeight)
              ) + 'px';
            }
          };

          const finish = () => {
            handle.removeEventListener('pointermove', move);
            handle.removeEventListener('pointerup', finish);
            handle.removeEventListener('pointercancel', finish);

            const all = getItems();
            const record = all.find(
              element => element.handle === item.handle
            );

            if (record) {
              Object.assign(record, {
                x: card.offsetLeft,
                y: card.offsetTop,
                width: card.offsetWidth,
                height: card.offsetHeight
              });

              persist(all);
            }
          };

          handle.addEventListener('pointermove', move);
          handle.addEventListener('pointerup', finish, { once: true });
          handle.addEventListener('pointercancel', finish, { once: true });
        };

        card.querySelectorAll('[data-drag]').forEach(element => {
          element.addEventListener(
            'pointerdown',
            event => beginDrag(event, false)
          );
        });

        card.querySelector('[data-resize]').addEventListener(
          'pointerdown',
          event => beginDrag(event, true)
        );

        canvas.append(card);

        const variants = product.variants || [];
        const row = document.createElement('div');

        row.className = 'dw__summary-item';
        row.dataset.summaryHandle = item.handle;

        row.innerHTML = `
          ${img
            ? `<img class="dw__summary-img"
                     src="${escapeHTML(img)}" alt="">`
            : '<span></span>'}

          <div>
            <div class="dw__summary-name">
              ${escapeHTML(product.title)}
            </div>

            <div class="dw__summary-controls">
              <label>
                Qty
                <input
                  type="number"
                  min="1"
                  max="99"
                  step="1"
                  data-qty
                  value="${clamp(Number(item.quantity) || 1, 1, 99)}"
                >
              </label>

              <label>
                Variant
                <select data-variant class="dw__summary-variant">
                  ${variants.map(variant => `
                    <option
                      value="${variant.id}"
                      ${Number(item.variantId) === variant.id
                        ? 'selected' : ''}
                      ${!variant.available ? 'disabled' : ''}
                    >
                      ${escapeHTML(
                        variant.public_title ||
                        variant.title ||
                        'Default'
                      )}
                      ${variant.available ? '' : '(Sold out)'}
                    </option>
                  `).join('')}
                </select>
              </label>
            </div>

            <button
              type="button"
              class="dw__summary-remove"
              data-remove
            >
              Remove from wall
            </button>
          </div>
        `;

        row.querySelector('[data-remove]').onclick =
          () => removeItem(item.handle);

        const selection = row.querySelector('[data-variant]');

        if (!variants.some(
          variant =>
            variant.id === Number(item.variantId) &&
            variant.available
        )) {
          selection.value = String(
            variants.find(variant => variant.available)?.id || ''
          );
        }

        const update = () => {
          const all = getItems();
          const record = all.find(
            element => element.handle === item.handle
          );

          if (record) {
            record.quantity = clamp(
              Number(row.querySelector('[data-qty]').value) || 1,
              1,
              99
            );

            record.variantId = Number(selection.value) || 0;
            persist(all);
          }
        };

        row.querySelector('[data-qty]').addEventListener('change', update);
        selection.addEventListener('change', update);

        summaryItems.append(row);
      });
    };

    section.querySelector('[data-dw-summary-close]').onclick = () => {
      summary.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
    };

    toggle.onclick = () => {
      summary.hidden = !summary.hidden;
      toggle.setAttribute('aria-expanded', String(!summary.hidden));
    };

    section.querySelector('[data-dw-add-all]').onclick = async event => {
      const button = event.currentTarget;
      button.disabled = true;
      notice.textContent = 'Adding products…';

      try {
        const items = getItems();

        if (!items.length) {
          throw Error('Add some products to your wall first.');
        }

        const merged = new Map();

        for (const item of items) {
          const product = await fetchProduct(item.handle);

          const variant =
            product.variants.find(
              v => v.id === Number(item.variantId) && v.available
            ) ||
            product.variants.find(v => v.available);

          if (!variant) throw Error(`${product.title} is sold out.`);

          merged.set(
            variant.id,
            (merged.get(variant.id) || 0) +
              clamp(Number(item.quantity) || 1, 1, 99)
          );
        }

        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [...merged].map(([id, quantity]) => ({
              id,
              quantity
            }))
          })
        });

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw Error(
            body.description ||
            body.message ||
            'Could not add products to cart.'
          );
        }

        notice.textContent = 'Added to cart!';
        window.location.href = `${root}cart`;
      } catch (error) {
        notice.textContent =
          error.message || 'Something went wrong.';
      } finally {
        button.disabled = false;
      }
    };

    let snapshot = JSON.stringify(
      getItems().map(item => item.handle)
    );

    window.addEventListener('design-wall:updated', () => {
      const next = JSON.stringify(
        getItems().map(item => item.handle)
      );

      if (next !== snapshot) {
        snapshot = next;
        render();
      }
    });

    await render();
  };

  const boot = () => {
    document.querySelectorAll('[data-design-wall]').forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();



document.addEventListener('DOMContentLoaded', () => {
  const header = document.getElementById('dwStickyHeader');
  const cartButton = document.getElementById('dwHeaderCart');
  const counter = document.getElementById('dwHeaderCount');

  if (!header) return;

  const updateHeader = () => {
    header.classList.toggle('is-visible', window.scrollY > 150);

    if (counter && window.DesignWallStore) {
      counter.textContent = window.DesignWallStore.read().length;
    }
  };

  window.addEventListener('scroll', updateHeader, {
    passive: true
  });

  window.addEventListener('design-wall:updated', updateHeader);

  cartButton?.addEventListener('click', () => {
    const summary = document.querySelector('[data-dw-summary]');
    const toggle = document.querySelector('[data-dw-summary-toggle]');

    if (summary) {
      summary.hidden = false;
      toggle?.setAttribute('aria-expanded', 'true');
    }
  });

  updateHeader();


setTimeout(function() {
  document.querySelectorAll('.dw__card-print').forEach(function(button) {
    button.addEventListener('click', function(e) {
      e.preventDefault();

      const productId = this.dataset.productId;

      const image = document.querySelector(
        `.dw__card-image img[data-product-id="${productId}"]`
      );

      if (!image) return;

      const customImageUrl = image.src;

      const printWindow = window.open('', '_blank');

      if (!printWindow) return;

      printWindow.document.write(`
        <!doctype html>
        <html>
          <head>
            <title>Print Image</title>

            <style>
              html,
              body {
                margin: 0;
                padding: 0;
              }

              body {
                display: flex;
                align-items: center;
                justify-content: center;
                min-height: 100vh;
              }

              img {
                display: block;
                max-width: 100%;
                max-height: 100vh;
                width: auto;
                height: auto;
                object-fit: contain;
              }

              @media print {
                @page {
                  margin: 0;
                }

                body {
                  margin: 0;
                }
              }
            </style>
          </head>

          <body>
            <img src="${customImageUrl}" alt="Product Image">

            <script>
              const image = document.querySelector('img');

              image.onload = function() {
                window.focus();
                window.print();
              };
            <\/script>
          </body>
        </html>
      `);

      printWindow.document.close();
    });
  });
}, 1000);
});


document.addEventListener('DOMContentLoaded', () => {
  const printButton = document.querySelector('[data-dw-print]');
  const canvas = document.querySelector('.dw__canvas');

  if (!printButton || !canvas) return;

  printButton.addEventListener('click', () => {
    const printWindow = window.open('', '_blank', 'width=1200,height=900');

    if (!printWindow) return;

    // Copy all stylesheets from the current Shopify page
    const styles = Array.from(
      document.querySelectorAll('link[rel="stylesheet"], style')
    )
      .map((style) => style.outerHTML)
      .join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Design Wall</title>

          ${styles}

          <style>
            @page {
              margin: 10mm;
            }

            html,
            body {
              margin: 0;
              padding: 0;
              background: #fff;
            }

            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            .dw-print-wrapper {
              width: 100%;
            }

            .dw__canvas {
              width: 100% !important;
              margin: 0 !important;
              box-shadow: none !important;
            }

            /* Hide UI controls inside products while printing */
            button,
            .dw-item__remove,
            .dw-item__resize,
            [data-dw-remove],
            [data-dw-resize] {
              display: none !important;
            }
          </style>
        </head>

        <body>
          <div class="dw-print-wrapper">
            ${canvas.outerHTML}
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();

    // Wait for images/fonts/styles before printing
    printWindow.onload = () => {
      const images = Array.from(printWindow.document.images);

      const imagePromises = images.map((image) => {
        if (image.complete) {
          return Promise.resolve();
        }

        return new Promise((resolve) => {
          image.onload = resolve;
          image.onerror = resolve;
        });
      });

      Promise.all(imagePromises).then(() => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      });
    };
  });
});